import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Locale, LocalizedText } from '@/types';

/** Tailwind-aware className combiner (shadcn convention). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const krwFormatter = new Intl.NumberFormat('ko-KR', {
  style: 'currency',
  currency: 'KRW',
  maximumFractionDigits: 0,
});

/** Format an integer amount as KRW (no decimals). */
export function formatKrw(amount: number): string {
  return krwFormatter.format(amount);
}

/** Resolve a localized field for the active locale, falling back to ko. */
export function localized(text: LocalizedText, locale: Locale): string {
  return text[locale] || text.ko || text.en;
}

const rtf = new Intl.RelativeTimeFormat('ko', { numeric: 'auto' });

/** Human-readable countdown to a deadline, Asia/Seoul semantics. */
export function timeUntil(iso: string, now: number = Date.now()): string {
  const diffMs = new Date(iso).getTime() - now;
  if (diffMs <= 0) return '마감됨';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
}

/** Returns h/m/s components for a live countdown timer. */
export function countdownParts(iso: string, now: number = Date.now()) {
  const total = Math.max(0, new Date(iso).getTime() - now);
  const totalSeconds = Math.floor(total / 1000);
  return {
    expired: total <= 0,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/** Progress ratio (0..1) of a bundle toward its target count. */
export function progressRatio(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(1, current / target);
}
